import React from 'react';

import { Responsive, responsiveClasses, withBaseDefault } from '../helpers/responsive';
import { SEMANTIC_TEXT_COLOR } from '../../design-tokens/build/rc-tokens-web-typescript';
import {
  propOptions,
  propDefaults,
  getClassnames,
  modifierClasses,
  staticVariantClass,
  variantToClasses,
  CanonicalTextStyle,
  TextTheme,
} from './textVariants';

export type { CanonicalTextStyle } from './textVariants';

type FontWeights = 'regular' | 'medium' | 'semibold' | 'bold';
type FontSizes = '300' | '350' | '400' | '450' | '500' | '550' | '600' | '700' | '800' | '900' | '1000' | '1200' | '1600';
type AllowedHtmlTags = 'div' | 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
type VALID_TEXT_COLORS = typeof SEMANTIC_TEXT_COLOR[number];

interface RCTextProps {
  /** Specifies the HTML tag used by the text element. Defaults to 'div'.
   *  Options are limited by text style.
  */
  htmlTag?: AllowedHtmlTags,
  /** Font size. Options are limited by text style. */
  size?: FontSizes,
  /** Font weight. Options are limited by text style. */
  weight?: FontWeights,
  /** Optionally, specify a text color. Inherits color by default. */
  color?: VALID_TEXT_COLORS,
  /** Optionally, pass a numeral to truncate text to the specified number of lines. */
  maxLines?: 1 | 2 | 3 | 4 | 5,
  /** Passes through to the htmlTag. */
  id?: string;
  /** Themed-surface text role. `emphasis` renders the surface's family-colored
   *  accessible text (e.g. notice text on a notice-ultralight fill). Only meaningful
   *  inside a themed surface (RCThemedBox, RCCallout, or a `.themed-box`); falls back
   *  to the surface's body text elsewhere. */
  theme?: TextTheme,
  /** Escape hatch for adjacent, non-text-style properties the typed props don't cover
   *  (`text-transform`, `text-align`, overflow/ellipsis, spacing) — matching RCFlex.
   *  Prefer the typed props for the component's own domain (variant / size / weight). */
  className?: string,
}

type DefaultTextProps = RCTextProps & {
  /** The base is the general text primitive, so its tag is NOT constrained by variant
   *  (unlike the subcomponents): any allowed tag is valid, since a responsive/cross-variant
   *  `variant` (e.g. a `title-*`/`display-*` style) routinely wants a semantic heading tag
   *  like `h1`. Tag = semantics, variant = presentation — the two are decoupled. */
  htmlTag?: AllowedHtmlTags;
  /** @deprecated Superseded by `variant` (`size="350"` → `variant="default-350"`).
   *  Retained for backward compatibility; ignored when `variant` is set. */
  size?: Extract<FontSizes, typeof propOptions.default.sizes[number]>;
  /** @deprecated Superseded by `variant`. Retained for backward compatibility;
   *  ignored when `variant` is set. */
  weight?: Extract<FontWeights, typeof propOptions.default.weights[number]>;
  /** The text style, as a flat `{variant}-{size}[-{weight}]` token — optionally
   *  responsive (`{ desktop, tablet, mobile }`). This is the general text primitive:
   *  it can render ANY variant (including cross-variant switches per breakpoint, e.g.
   *  `{ desktop: 'display-1600', mobile: 'title-900' }`) — the escape from a
   *  `useMediaQuery` render-swap. The subcomponents (`RCText.Title`, …) are static
   *  sugar over this. Defaults to `default-400`. */
  variant?: Responsive<CanonicalTextStyle>;
};

type DisplayTextProps = RCTextProps & {
  htmlTag?: Extract<AllowedHtmlTags, typeof propOptions.display.htmlTags[number]>;
  size?: Extract<FontSizes, typeof propOptions.display.sizes[number]>;
  weight?: Extract<FontWeights, typeof propOptions.display.weights[number]>;
};

export type EyebrowTextProps = RCTextProps & {
  htmlTag?: Extract<AllowedHtmlTags, typeof propOptions.eyebrow.htmlTags[number]>;
  size?: Extract<FontSizes, typeof propOptions.eyebrow.sizes[number]>;
  weight?: Extract<FontWeights, typeof propOptions.eyebrow.weights[number]>;
};

type FineTextProps = RCTextProps & {
  htmlTag?: Extract<AllowedHtmlTags, typeof propOptions.fine.htmlTags[number]>;
  size?: Extract<FontSizes, typeof propOptions.fine.sizes[number]>;
  weight?: Extract<FontWeights, typeof propOptions.fine.weights[number]>;
};

type TitleTextProps = RCTextProps & {
  htmlTag?: Extract<AllowedHtmlTags, typeof propOptions.title.htmlTags[number]>;
  size: Extract<FontSizes, typeof propOptions.title.sizes[number]>;
  weight?: Extract<FontWeights, typeof propOptions.title.weights[number]>;
};

type UtilityTextProps = RCTextProps & {
  htmlTag?: Extract<AllowedHtmlTags, typeof propOptions.utility.htmlTags[number]>;
  size: Extract<FontSizes, typeof propOptions.utility.sizes[number]>;
  weight?: Extract<FontWeights, typeof propOptions.utility.weights[number]>;
};

/**
 *  Utility component — renders text through the canonical `text-*` utility classes via a
 *  constrained size / weight / variant API (and picks the semantic tag). No structural contract.
 *
 *  Use this component for displaying text. Dot notation variants allow for more specific styling cases:
 *
 *  - RCText (default): The general text primitive. Its flat `variant` prop renders any
 *    text style and is the only one that goes responsive (per-breakpoint / cross-variant).
 *  - RCText.**Display**: Extra large responsive text used for page headings. Often uses an `h1` HTML tag.
 *  - RCText.**Eyebrow**: Small and uppercase text. Often uses a `span` tag.
 *  - RCText.**Fine**: Very small text. Used for disclaimers.
 *  - RCText.**Title**: Large responsive text used for headings within a page layout. Often uses an `h1 - h6` HTML tag.
 *  - RCText.**Utility**: Flexible size and weight options with a shorter line height. Especially useful in UI compositions.
 */
export function RCText({
  htmlTag = 'div',
  variant = undefined,
  size = '400',
  weight = 'regular',
  color = undefined,
  maxLines = undefined,
  id = undefined,
  theme = undefined,
  className = undefined,
  children,
}: React.PropsWithChildren<DefaultTextProps>) {
  const Tag = htmlTag as React.ElementType;
  const styleOverrides = {
    ...colorOverride(color),
  } as React.CSSProperties;

  // `variant` (responsive-capable) supersedes the legacy `size`/`weight` props. When
  // it's absent we reconstruct the equivalent `default` token so behavior is unchanged.
  // `withBaseDefault` injects `default-400` at the desktop tier for a partial object that
  // omits it (e.g. `{ mobile: … }`), so the base keeps the component's default style
  // rather than rendering with no `text-*` class above the specified breakpoint —
  // preserving the "defaults to default-400" contract and the desktop-first cascade.
  const textClasses = variant !== undefined
    ? responsiveClasses(withBaseDefault(variant, 'default-400'), variantToClasses)
    : [staticVariantClass('default', size, weight)];

  return (
    <Tag
      id={id}
      className={modifierClasses(textClasses, maxLines, theme, className)}
      style={styleOverrides}
    >
      {children}
    </Tag>
  );
}

export function RCTextDisplay({
  htmlTag = 'div',
  size = '1600',
  weight = 'bold',
  color = undefined,
  maxLines = undefined,
  id = undefined,
  theme = undefined,
  className = undefined,
  children,
}: React.PropsWithChildren<DisplayTextProps>) {
  const Tag = htmlTag as React.ElementType;
  const styleOverrides = {
    ...colorOverride(color),
  } as React.CSSProperties;

  return (
    <Tag
      id={id}
      className={getClassnames('display', size, weight, maxLines, theme, className)}
      style={styleOverrides}
    >
      {children}
    </Tag>
  );
}

function RCTextEyebrow({
  htmlTag = 'div',
  size = '300',
  weight = 'bold',
  color = undefined,
  maxLines = undefined,
  id = undefined,
  theme = undefined,
  className = undefined,
  children,
}: React.PropsWithChildren<EyebrowTextProps>) {
  const Tag = htmlTag as React.ElementType;
  const styleOverrides = {
    ...colorOverride(color),
  } as React.CSSProperties;

  return (
    <Tag
      id={id}
      className={getClassnames('eyebrow', size, weight, maxLines, theme, className)}
      style={styleOverrides}
    >
      {children}
    </Tag>
  );
}

function RCTextFine({
  htmlTag = 'div',
  size = '300',
  weight = 'regular',
  color = undefined,
  maxLines = undefined,
  id = undefined,
  theme = undefined,
  className = undefined,
  children,
}: React.PropsWithChildren<FineTextProps>) {
  const Tag = htmlTag as React.ElementType;
  const styleOverrides = {
    ...colorOverride(color),
  } as React.CSSProperties;

  return (
    <Tag
      id={id}
      className={getClassnames('fine', size, weight, maxLines, theme, className)}
      style={styleOverrides}
    >
      {children}
    </Tag>
  );
}

export function RCTextTitle({
  htmlTag = 'div',
  size = '900',
  weight = 'bold',
  color = undefined,
  maxLines = undefined,
  id = undefined,
  theme = undefined,
  className = undefined,
  children,
}: React.PropsWithChildren<TitleTextProps>) {
  const Tag = htmlTag as React.ElementType;
  const styleOverrides = {
    ...colorOverride(color),
  } as React.CSSProperties;

  return (
    <Tag
      id={id}
      className={getClassnames('title', size, weight, maxLines, theme, className)}
      style={styleOverrides}
    >
      {children}
    </Tag>
  );
}

export function RCTextUtility({
  htmlTag = 'div',
  size = '400',
  weight = 'regular',
  color = undefined,
  maxLines = undefined,
  id = undefined,
  theme = undefined,
  className = undefined,
  children,
}: React.PropsWithChildren<UtilityTextProps>) {
  const Tag = htmlTag as React.ElementType;
  const styleOverrides = {
    ...colorOverride(color),
  } as React.CSSProperties;

  return (
    <Tag
      id={id}
      className={getClassnames('utility', size, weight, maxLines, theme, className)}
      style={styleOverrides}
    >
      {children}
    </Tag>
  );
}

function colorOverride(color: VALID_TEXT_COLORS) {
  return color === undefined ? {} : { 'color': `var(--${color})` };
}

RCText.Display = RCTextDisplay;
RCText.Eyebrow = RCTextEyebrow;
RCText.Fine = RCTextFine;
RCText.Title = RCTextTitle;
RCText.Utility = RCTextUtility;


// Used by Storybook

interface validOutputProps {
  [variant: string]: {
    htmlTags: readonly string[],
    sizes: readonly string[],
    weights: readonly string[],
  }
}

const outputProps = propOptions as validOutputProps;

const combinedVariants = Object.keys(outputProps).map(variant => ({
  [variant]: {
    id: variant,
    weights: Array.prototype.reverse.call([...outputProps[variant].weights]),
    sizes: Array.prototype.reverse.call([...outputProps[variant].sizes]),
    defaultWeight: propDefaults[variant].weight,
    defaultSize: propDefaults[variant].size,
  },
}));

// data object for storybook
export const TextVariantProperties = combinedVariants.reduce((output, variant) => ({ ...output, ...variant }), {});
